import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { League, leaguesApi, Sport, sportsApi } from '../services/sportsLeaguesService';
import Dropdown, { DropdownOption } from './Dropdown';
import { translateEvent } from '../utils/translateEvent';

export interface FilterValues {
  selectedSport: string;
  selectedLeague: string;
  minOdds: string;
  maxOdds: string;
  minStake: string;
  maxStake: string;
  eventsNumber: string;
  fromDate: string;
  toDate: string;
}

interface FiltersProps {
  onFiltersChange: (filters: FilterValues) => void;
  onClear: () => void;
  isDemo?: boolean;
  hideEventsNumber?: boolean;
  layout?: 'generator' | 'feed';
}

const Filters: React.FC<FiltersProps> = ({
  onFiltersChange,
  onClear,
  isDemo = false,
  hideEventsNumber = false,
  layout = 'generator',
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedLeague, setSelectedLeague] = useState<string>('');
  const [minOdds, setMinOdds] = useState<string>('');
  const [maxOdds, setMaxOdds] = useState<string>('');
  const [minStake, setMinStake] = useState<string>('');
  const [maxStake, setMaxStake] = useState<string>('');
  const [eventsNumber, setEventsNumber] = useState<string>('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const [sports, setSports] = useState<Sport[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [loadingSports, setLoadingSports] = useState(true);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  useEffect(() => {
    const fetchSports = async () => {
      try {
        setLoadingSports(true);
        const sportsData = await sportsApi.getAll();
        setSports(sportsData);
      } catch (error) {
        console.error('Error fetching sports:', error);
      } finally {
        setLoadingSports(false);
      }
    };
    fetchSports();
  }, []);

  useEffect(() => {
    const fetchLeagues = async () => {
      if (selectedSport) {
        try {
          setLoadingLeagues(true);
          const sportId = parseInt(selectedSport);
          const leaguesData = await leaguesApi.getAll(sportId);
          setLeagues(leaguesData);
          setSelectedLeague('');
        } catch (error) {
          console.error('Error fetching leagues:', error);
        } finally {
          setLoadingLeagues(false);
        }
      } else {
        setLeagues([]);
        setSelectedLeague('');
      }
    };
    fetchLeagues();
  }, [selectedSport]);

  useEffect(() => {
    pushFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport, selectedLeague, fromDate, toDate]);

  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const pushFilters = () => {
    onFiltersChange({
      selectedSport,
      selectedLeague,
      minOdds,
      maxOdds,
      minStake,
      maxStake,
      eventsNumber,
      fromDate: fromDate ? toLocalDateStr(fromDate) : '',
      toDate: toDate ? toLocalDateStr(toDate) : '',
    });
  };

  const handleClear = () => {
    setSelectedSport('');
    setSelectedLeague('');
    setMinOdds('');
    setMaxOdds('');
    setMinStake('');
    setMaxStake('');
    setEventsNumber('');
    setFromDate(null);
    setToDate(null);
    onClear();
  };

  const isFeed = layout === 'feed';
  const optFieldCls = isFeed ? 'rec-feed-filters__field' : 'generator__option-field';
  const labelCls = isFeed ? 'rec-feed-filters__label' : 'generator__field-label';
  const dateWrapCls = isFeed
    ? 'filters__datepicker-wrapper rec-feed-filters__datepicker-wrap'
    : 'filters__datepicker-wrapper';

  const sportOptions: DropdownOption[] = loadingSports
    ? [{ value: '', label: t.filters.loadingSports, disabled: true }]
    : [
        { value: '', label: t.filters.selectSport },
        ...sports.map((sport) => ({
          value: sport.id.toString(),
          label: t.sports[sport.name] ?? sport.name,
          icon: sport.icon,
        })),
      ];

  const leagueOptions: DropdownOption[] = loadingLeagues
    ? [{ value: '', label: t.filters.loadingLeagues, disabled: true }]
    : leagues.length === 0
      ? [
          {
            value: '',
            label: selectedSport ? t.filters.noLeaguesAvailable : t.filters.selectSportFirst,
            disabled: true,
          },
        ]
      : [
          { value: '', label: t.filters.selectLeague },
          ...leagues.map((league) => ({
            value: league.id.toString(),
            label: translateEvent(league.name, t.eventsDictionary),
          })),
        ];

  const sportField = (
    <div className={isFeed ? 'rec-feed-filters__field' : 'generator__market-field'}>
      <label className={labelCls} htmlFor="sport-select">{t.filters.sport}</label>
      <Dropdown
        id="sport-select"
        className="filters__select"
        value={selectedSport}
        onChange={setSelectedSport}
        options={sportOptions}
        placeholder={t.filters.selectSport}
        title={t.filters.sport}
        disabled={loadingSports}
      />
    </div>
  );

  const leagueField = (
    <div className={isFeed ? 'rec-feed-filters__field' : 'generator__market-field'}>
      <label className={labelCls} htmlFor="league-select">{t.filters.league}</label>
      <Dropdown
        id="league-select"
        className="filters__select"
        value={selectedLeague}
        onChange={setSelectedLeague}
        options={leagueOptions}
        placeholder={t.filters.selectLeague}
        title={t.filters.league}
        disabled={!selectedSport || loadingLeagues}
      />
    </div>
  );

  const advancedFields = !isDemo && (
    <>
      {!hideEventsNumber && (
        <div className={optFieldCls}>
          <label className={labelCls} htmlFor="events-number">{t.filters.eventsNumber}</label>
          <input
            id="events-number"
            className="filters__input"
            type="number"
            min="1"
            max={isAuthenticated ? "50" : "10"}
            step="1"
            value={eventsNumber}
            onChange={(e) => setEventsNumber(e.target.value)}
            onBlur={pushFilters}
          />
        </div>
      )}

      <div className={optFieldCls}>
        <label className={labelCls} htmlFor="min-odds">{t.filters.minOdds}</label>
        <input
          id="min-odds"
          className="filters__input"
          type="number"
          min="1"
          step="0.1"
          value={minOdds}
          onChange={(e) => setMinOdds(e.target.value)}
          onBlur={pushFilters}
        />
      </div>

      <div className={optFieldCls}>
        <label className={labelCls} htmlFor="max-odds">{t.filters.maxOdds}</label>
        <input
          id="max-odds"
          className="filters__input"
          type="number"
          min="1"
          step="0.1"
          value={maxOdds}
          onChange={(e) => setMaxOdds(e.target.value)}
          onBlur={pushFilters}
        />
      </div>

      {isFeed && (
        <>
          <div className={optFieldCls}>
            <label className={labelCls} htmlFor="min-stake">{t.filters.minStake}</label>
            <input
              id="min-stake"
              className="filters__input"
              type="number"
              min="0"
              step="0.01"
              value={minStake}
              onChange={(e) => setMinStake(e.target.value)}
              onBlur={pushFilters}
            />
          </div>
          <div className={optFieldCls}>
            <label className={labelCls} htmlFor="max-stake">{t.filters.maxStake}</label>
            <input
              id="max-stake"
              className="filters__input"
              type="number"
              min="0"
              step="0.01"
              value={maxStake}
              onChange={(e) => setMaxStake(e.target.value)}
              onBlur={pushFilters}
            />
          </div>
        </>
      )}

      <div className={optFieldCls}>
        <label className={labelCls} htmlFor="from-date">{t.filters.fromDate}</label>
        <DatePicker
          id="from-date"
          selected={fromDate}
          onChange={(date: Date | null) => setFromDate(date)}
          dateFormat="yyyy-MM-dd"
          className="filters__datepicker"
          wrapperClassName={dateWrapCls}
        />
      </div>

      <div className={optFieldCls}>
        <label className={labelCls} htmlFor="to-date">{t.filters.toDate}</label>
        <DatePicker
          id="to-date"
          selected={toDate}
          onChange={(date: Date | null) => setToDate(date)}
          dateFormat="yyyy-MM-dd"
          minDate={fromDate || undefined}
          className="filters__datepicker"
          wrapperClassName={dateWrapCls}
        />
      </div>
    </>
  );

  const clearRow = (
    <div className={isFeed ? 'rec-feed-filters__clear' : 'generator__clear-row'}>
      <button
        type="button"
        className={isFeed ? 'rec-feed-filters__clear-btn' : 'generator__clear-btn'}
        onClick={handleClear}
      >
        {t.filters.clearFilters}
      </button>
    </div>
  );

  if (isFeed) {
    return (
      <div className="rec-feed-filters">
        <div className="rec-feed-filters__grid rec-feed-filters__grid--primary">
          {sportField}
          {leagueField}
        </div>

        {!isDemo && (
          <div
            className={`rec-feed-filters__more${moreFiltersOpen ? ' rec-feed-filters__more--open' : ''}`}
          >
            <button
              type="button"
              className="rec-feed-filters__more-toggle"
              aria-expanded={moreFiltersOpen}
              aria-controls="rec-feed-filters-more-body"
              id="rec-feed-filters-more-toggle"
              onClick={() => setMoreFiltersOpen((o) => !o)}
            >
              <span>{t.filters.moreFilters}</span>
              <span className="rec-feed-filters__more-chevron" aria-hidden />
            </button>
            <div
              id="rec-feed-filters-more-body"
              className="rec-feed-filters__more-body"
              role="region"
              aria-labelledby="rec-feed-filters-more-toggle"
              hidden={!moreFiltersOpen}
            >
              <div className="rec-feed-filters__grid">{advancedFields}</div>
            </div>
          </div>
        )}

        {clearRow}
      </div>
    );
  }

  return (
    <>
      <div className="generator__market-card">
        {sportField}
        {leagueField}
      </div>

      {!isDemo && (
        <div className="generator__options-row">
          {advancedFields}
        </div>
      )}

      {clearRow}
    </>
  );
};

export default Filters;
